import React, { useState, useMemo } from 'react';
import { TrendingUp, Scale, CircleDollarSign } from 'lucide-react';
import { Card } from '../ui';
import { Input } from '../ui';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

export function UnionWageProgression() {
  const [currentWage, setCurrentWage] = useState<number>(25.00);
  const [statusQuoRaise, setStatusQuoRaise] = useState<number>(2.0); // %
  const [proposedCOLA, setProposedCOLA] = useState<number>(4.5); // %
  const [unionDues, setUnionDues] = useState<number>(1.5); // %
  const [years, setYears] = useState<number>(5);

    const model = useMemo(() => {
    const data = [];
    let cumulativeDifference = 0;
    let totalDuesPaid = 0;
    
    // Assume 2080 hours in a working year
    const HOURS_PER_YEAR = 2080;

    let sqWage = currentWage;
    let propWage = currentWage;

    for (let yr = 1; yr <= years; yr++) {
      if (yr > 1) {
        sqWage = sqWage * (1 + statusQuoRaise / 100);
        propWage = propWage * (1 + proposedCOLA / 100);
      }

      const sqAnnual = sqWage * HOURS_PER_YEAR;
      const propAnnual = propWage * HOURS_PER_YEAR;
      const duesAnnual = propAnnual * (unionDues / 100);
      const diffAnnual = propAnnual - sqAnnual;
      const netAdvantageAnnual = diffAnnual - duesAnnual;
      
      cumulativeDifference += netAdvantageAnnual;
      totalDuesPaid += duesAnnual;

      data.push({
        year: `Year ${yr}`,
        statusQuo: Number(sqWage.toFixed(2)),
        proposed: Number(propWage.toFixed(2)),
        diff: Number(diffAnnual.toFixed(0)),
        netAdvantage: Number(netAdvantageAnnual.toFixed(0))
      });
    }

    const finalWageSq = data[data.length - 1].statusQuo;
    const finalWageProp = data[data.length - 1].proposed;

    return {
      data,
      cumulativeDifference,
      totalDuesPaid,
      finalWageSq,
      finalWageProp,
      increase: finalWageProp - finalWageSq
    };
  }, [currentWage, statusQuoRaise, proposedCOLA, unionDues, years]);

  const formatCurrency = (val: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);

  return (
    <div className="max-w-6xl mx-auto space-y-8 p-4">
      <div className="bg-gradient-to-r from-amber-600 to-orange-700 rounded-2xl p-8 text-white shadow-xl flex flex-col md:flex-row items-center justify-between gap-6">
        <div>
          <h2 className="text-2xl font-black flex items-center mb-2">
            <Scale className="w-6 h-6 mr-3 text-amber-200" />
            CBA Wage Progression Modeler
          </h2>
          <p className="text-amber-100 max-w-xl">
            Visualize the compounding power of collective bargaining. Compare the proposed contract's Cost of Living Adjustments (COLA) against the employer's status quo over the life of the contract.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-4 gap-8">
        
        {/* Controls */}
        <div className="md:col-span-1 space-y-6">
          <Card className="p-6 space-y-6 border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900">
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-2">
                Current Base Wage ($/hr)
              </label>
              <Input 
                type="number" 
                step="0.5"
                value={currentWage} 
                onChange={(e) => setCurrentWage(Number(e.target.value))}
                className="text-lg font-bold"
              />
            </div>

            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-1">
                Status Quo Raise (%)
              </label>
              <p className="text-[10px] text-slate-400 mb-2 leading-tight">The estimated yearly raise without a new union contract (e.g., a standard 2% baseline).</p>
              <div className="flex items-center gap-3">
                <input 
                  type="range" 
                  min="0" 
                  max="10" 
                  step="0.5"
                  value={statusQuoRaise} 
                  onChange={(e) => setStatusQuoRaise(Number(e.target.value))}
                  className="flex-1 accent-amber-600"
                />
                <span className="font-bold w-12 text-right">{statusQuoRaise}%</span>
              </div>
            </div>

            <div className="pt-4 border-t border-slate-100 dark:border-slate-800">
              <label className="text-xs font-bold text-amber-600 uppercase tracking-wider block mb-1">
                Proposed Union COLA (%)
              </label>
              <p className="text-[10px] text-amber-600/70 mb-2 leading-tight">The yearly raise negotiated in the new collective bargaining agreement.</p>
              <div className="flex items-center gap-3">
                <input 
                  type="range" 
                  min="0" 
                  max="15" 
                  step="0.5"
                  value={proposedCOLA} 
                  onChange={(e) => setProposedCOLA(Number(e.target.value))}
                  className="flex-1 accent-amber-600"
                />
                <span className="font-bold w-12 text-right">{proposedCOLA}%</span>
              </div>
            </div>

            <div className="pt-4 border-t border-slate-100 dark:border-slate-800">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-1">
                Union Dues (%)
              </label>
              <p className="text-[10px] text-slate-400 mb-2 leading-tight">Typically 1-2%. Used to calculate your true net advantage after dues are paid.</p>
              <div className="flex items-center gap-3">
                <input 
                  type="range" 
                  min="0" 
                  max="5" 
                  step="0.1"
                  value={unionDues} 
                  onChange={(e) => setUnionDues(Number(e.target.value))}
                  className="flex-1 accent-slate-600"
                />
                <span className="font-bold w-12 text-right">{unionDues}%</span>
              </div>
            </div>

            <div className="pt-4 border-t border-slate-100 dark:border-slate-800">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-2">
                Contract Length (Years)
              </label>
              <div className="flex items-center gap-3">
                <input 
                  type="range" 
                  min="1" 
                  max="10" 
                  step="1"
                  value={years} 
                  onChange={(e) => setYears(Number(e.target.value))}
                  className="flex-1 accent-slate-600"
                />
                <span className="font-bold w-12 text-right">{years}</span>
              </div>
            </div>
          </Card>
        </div>

        {/* Visuals */}
        <div className="md:col-span-3 space-y-6">
          
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="p-6 border-amber-200 dark:border-amber-900 shadow-xl bg-amber-50 dark:bg-amber-900/10">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-sm font-bold text-amber-700 dark:text-amber-500 uppercase tracking-wider mb-1">Net Additional Earnings</h3>
                  <div className="text-4xl font-black text-amber-600 dark:text-amber-400">
                    {formatCurrency(model.cumulativeDifference)}
                  </div>
                </div>
                <CircleDollarSign className="w-8 h-8 text-amber-500 opacity-50" />
              </div>
              <p className="text-sm mt-4 text-amber-800 dark:text-amber-300">
                Total extra take-home pay per member over the {years}-year contract compared to the status quo, <strong>after paying {formatCurrency(model.totalDuesPaid)} in dues</strong>.
              </p>
            </Card>

            <Card className="p-6 border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900 flex flex-col justify-center">
              <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-1 flex justify-between">
                <span>Final Hourly Rate</span>
                <span className="text-emerald-500">+{formatCurrency(model.increase)}/hr</span>
              </h3>
              <div className="flex items-baseline gap-4 mt-2">
                <div className="text-4xl font-black text-slate-900 dark:text-white">
                  {formatCurrency(model.finalWageProp)}
                </div>
                <div className="text-xl font-bold text-slate-400 line-through">
                  {formatCurrency(model.finalWageSq)}
                </div>
              </div>
            </Card>
          </div>

          <Card className="p-6 border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900 h-[400px]">
            <h3 className="text-lg font-bold mb-6 flex items-center">
              <TrendingUp className="w-5 h-5 mr-2 text-amber-500" /> Hourly Rate Trajectory
            </h3>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={model.data} margin={{ top: 5, right: 20, bottom: 25, left: 20 }}>
                <defs>
                  <linearGradient id="colorProposed" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#d97706" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#d97706" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#334155" opacity={0.2} />
                <XAxis 
                  dataKey="year" 
                  tick={{ fill: '#64748b', fontSize: 12 }}
                  tickMargin={10}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis 
                  tickFormatter={(val) => `$${val}`}
                  tick={{ fill: '#64748b', fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                  domain={['dataMin - 2', 'dataMax + 2']}
                />
                <Tooltip 
                  formatter={(value: any) => formatCurrency(Number(value))}
                  contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                />
                <Legend verticalAlign="top" height={36}/>
                <Area 
                  type="monotone" 
                  dataKey="proposed" 
                  name="Proposed Contract"
                  stroke="#d97706" 
                  fillOpacity={1} 
                  fill="url(#colorProposed)" 
                  strokeWidth={3} 
                />
                <Area 
                  type="monotone" 
                  dataKey="statusQuo" 
                  name="Status Quo"
                  stroke="#94a3b8" 
                  fillOpacity={0} 
                  strokeWidth={3} 
                  strokeDasharray="5 5"
                />
              </AreaChart>
            </ResponsiveContainer>
          </Card>

        </div>
      </div>
    </div>
  );
}
